/**
 * NOLAS 2026, (c) Mikaël FOURNY.
 * src/pwa/rechargerVersionRecente.ts
 *
 * Rôle : recharger la page SUR LA NOUVELLE VERSION, et pas sur l'ancienne.
 *
 * Le défaut corrigé (banc d'intégration du 2026-10-04) : la surveillance de
 * `index.html` (versionWatch.ts) voyait la nouvelle version, affichait le
 * bandeau, et son bouton « Recharger » faisait `location.reload()`. Or tant
 * que l'ancien service worker contrôle la page, un rechargement ressert la
 * page depuis son précache : la personne revenait sur l'ancienne version, à
 * chaque fois, et le rechargement automatique à l'ouverture tournait à vide.
 * C'est exactement le symptôme de l'iPhone de Mika.
 *
 * Ce qui est fait ici, dans l'ordre :
 *   1. demander au navigateur de vérifier le service worker (update) ;
 *   2. si une version attend (ou s'installe), lui envoyer SKIP_WAITING, le
 *      message que notre sw.js comprend, puis recharger dès qu'elle a pris
 *      la main (controllerchange) ;
 *   3. sinon, ou si rien ne se passe en quelques secondes, recharger quand
 *      même : la page n'est jamais bloquée sur un bouton qui ne fait rien.
 *   Un seul rechargement, quoi qu'il arrive.
 *
 * Mikael - chef de projet - 2026-10-04
 */

/** Délai au-delà duquel on recharge sans attendre la prise de main. */
export const DELAI_SECOURS_MS = 4000;

type Dependances = {
  readonly sw: ServiceWorkerContainer | undefined;
  readonly recharger: () => void;
  readonly planifier: (f: () => void, ms: number) => unknown;
};

function dependancesParDefaut(): Dependances {
  return {
    sw: typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
    recharger: () => window.location.reload(),
    planifier: (f, ms) => window.setTimeout(f, ms),
  };
}

export async function rechargerVersionRecente(dep: Dependances = dependancesParDefaut()): Promise<void> {
  let fait = false;
  const uneFois = (): void => {
    if (!fait) {
      fait = true;
      dep.recharger();
    }
  };

  const sw = dep.sw;
  if (sw === undefined) {
    uneFois();
    return;
  }
  try {
    const reg = await sw.getRegistration();
    if (reg === undefined) {
      uneFois();
      return;
    }
    try {
      await reg.update();
    } catch {
      // Hors ligne ou serveur indisponible : on travaille avec ce qu'on a.
    }
    const enAttente = reg.waiting ?? reg.installing;
    if (enAttente === null) {
      uneFois();
      return;
    }
    sw.addEventListener('controllerchange', uneFois);
    const envoyer = (w: ServiceWorker): void => w.postMessage({ type: 'SKIP_WAITING' });
    if (reg.waiting !== null) {
      envoyer(reg.waiting);
    } else {
      enAttente.addEventListener('statechange', () => {
        if (enAttente.state === 'installed') envoyer(enAttente);
      });
    }
    dep.planifier(uneFois, DELAI_SECOURS_MS);
  } catch {
    uneFois();
  }
}
